"use client";

import React from "react";
import EditSection, { TextEditor } from "../../sections/shared/EditSection";
import { SectionContentProps } from "../../types";
import { Input } from "rizzui";
import { PiUploadSimple } from "react-icons/pi";
import { getImageUrl } from "@/utils/image-url";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";

export default function UniversityHeroEdit({
  content,
  onContentChange,
}: SectionContentProps) {
  const updateContent = (key: string, value: any) => {
    onContentChange?.({ ...content, [key]: value });
  };

  return (
    <EditSection
      content={content}
      onContentChange={onContentChange || (() => {})}
      contentFields={
        <div className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Section Content</h4>
            <TextEditor
              label="Tag Line"
              value={content.tag || ""}
              onChange={(v) => updateContent("tag", v)}
              placeholder="knowledge meets innovation"
            />
            <TextEditor
              label="Title"
              value={content.title}
              onChange={(v) => updateContent("title", v)}
              placeholder="Instacks University"
            />
            <TextEditor
              label="Highlight Text"
              value={content.highlightText || ""}
              onChange={(v) => updateContent("highlightText", v)}
              placeholder="OF INSTACKS"
            />
            <TextEditor
              label="Subtitle"
              value={content.subtitle}
              onChange={(v) => updateContent("subtitle", v)}
              placeholder="Remember to tailor the section names..."
              multiline
            />
          </div>

          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Primary Button</h4>
            <TextEditor
              label="CTA Text"
              value={content.primaryCta || ""}
              onChange={(v) => updateContent("primaryCta", v)}
              placeholder="View Our Program"
            />
            <TextEditor
              label="CTA Link"
              value={content.primaryCtaLink || ""}
              onChange={(v) => updateContent("primaryCtaLink", v)}
              placeholder="/admissions"
            />
          </div>

          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Secondary Button</h4>
            <TextEditor
              label="CTA Text"
              value={content.secondaryCta}
              onChange={(v) => updateContent("secondaryCta", v)}
              placeholder="Learn More"
            />
            <TextEditor
              label="CTA Link"
              value={content.secondaryCtaLink}
              onChange={(v) => updateContent("secondaryCtaLink", v)}
              placeholder="/learn-more"
            />
          </div>

          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">University Specials</h4>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">
                YouTube Video Link
              </label>
              <Input
                value={content.videoUrl || ""}
                onChange={(e) => updateContent("videoUrl", e.target.value)}
                placeholder="e.g., https://www.youtube.com/watch?v=7ahgosTZJHg"
                className="!rounded-lg"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">
                Spinning Circle Text
              </label>
              <Input
                value={content.circleText || ""}
                onChange={(e) => updateContent("circleText", e.target.value)}
                placeholder="e.g., * Explore Future *Instacks University * Estd. 1971 "
                className="!rounded-lg"
              />
            </div>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Hero Images</h4>
            <div>
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1 block">
                Left Hero Image
              </label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-12 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0 relative group/img">
                  {content.imageUrl ? (
                    <img
                      src={getImageUrl(content.imageUrl) || content.imageUrl}
                      alt="Left Hero Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      🖼️
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity cursor-pointer">
                    <PiUploadSimple className="w-4 h-4 text-white" />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error("Image must be under 5MB");
                          return;
                        }
                        const uploadPromise = homepageApi.uploadImage(file);
                        toast.promise(uploadPromise, {
                          loading: "Uploading left hero image...",
                          success: "Left hero image uploaded!",
                          error: "Failed to upload image",
                        });
                        const response = await uploadPromise;
                        if (response.success && response.data?.url) {
                          updateContent("imageUrl", response.data.url);
                        }
                      }}
                    />
                  </label>
                </div>
                <Input
                  value={content.imageUrl || ""}
                  onChange={(e) => updateContent("imageUrl", e.target.value)}
                  placeholder="Paste URL or upload image"
                  className="!rounded-lg text-xs flex-1 h-8"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1 block">
                Right Hero Image
              </label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-12 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0 relative group/img">
                  {content.imageUrl2 ? (
                    <img
                      src={getImageUrl(content.imageUrl2) || content.imageUrl2}
                      alt="Right Hero Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      🖼️
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity cursor-pointer">
                    <PiUploadSimple className="w-4 h-4 text-white" />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error("Image must be under 5MB");
                          return;
                        }
                        const uploadPromise = homepageApi.uploadImage(file);
                        toast.promise(uploadPromise, {
                          loading: "Uploading right hero image...",
                          success: "Right hero image uploaded!",
                          error: "Failed to upload image",
                        });
                        const response = await uploadPromise;
                        if (response.success && response.data?.url) {
                          updateContent("imageUrl2", response.data.url);
                        }
                      }}
                    />
                  </label>
                </div>
                <Input
                  value={content.imageUrl2 || ""}
                  onChange={(e) => updateContent("imageUrl2", e.target.value)}
                  placeholder="Paste URL or upload image"
                  className="!rounded-lg text-xs flex-1 h-8"
                />
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}
